import Box from '@mui/material/Box';
import { LoadScript, GoogleMap, OverlayView } from '@react-google-maps/api';
import { map, throttle } from 'lodash';
import { useMemo, useRef, useState, useCallback, useEffect, useDeferredValue } from 'react';
import { useTranslate } from 'react-admin';
import useGetCompany from '@/hooks/useGetCompany';
import useGetCurrentUserV2 from '@/hooks/useGetCurrentUser';
import useGetAvatarsViewport from '@/hooks/useGetAvatarsViewport';
import { composeUri } from '@/utils/compose';
import { AVATARS, GEOCODE_APIKEY, GOOGLE_MAPS_LIBRARIES } from '@/utils/CONST';
import CustomBlock from '@/components/common/custom-block/custom-block';
import CustomClusterMarker from '@/components/map/items/custom-cluster-marker';
import CustomMarker from '@/components/map/items/custom-marker';
import MapStackedItemsMarker from '@/components/map/items/map-stacked-items-marker';
import MapStackItemsPopover from '@/components/map/map-stack-items-popover';
import AvatarInfoDialog from '@/components/map/avatar-info-dialog';
import type { ViewportItem, ViewportResponse } from '@/hooks/useGetAvatarsViewport';
import { groupViewportItemsByPosition } from '@/components/map/utils/viewport-item-stacks';

const VIEWPORT_REQUEST_THROTTLE_MS = 1000;

type MapBoundsState = {
    selat: number;
    selng: number;
    nwlat: number;
    nwlng: number;
    nelat: number;
    nelng: number;
    swlat: number;
    swlng: number;
};

const MapMain = () => {
    const translate = useTranslate();
    const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null);
    const [stackPopover, setStackPopover] = useState<{
        anchorEl: HTMLElement | null;
        items: ViewportItem[];
    }>({ anchorEl: null, items: [] });

    const closeStackPopover = useCallback(() => {
        setStackPopover({ anchorEl: null, items: [] });
    }, []);

    const [bounds, setBounds] = useState<MapBoundsState>();
    const [zoom, setZoom] = useState(10);
    /** Parámetros efectivos para la petición viewport (throttle sobre bounds + zoom). */
    const [viewportQueryBounds, setViewportQueryBounds] = useState<MapBoundsState>();
    const [viewportQueryZoom, setViewportQueryZoom] = useState(10);

    const mapRef = useRef<google.maps.Map | null>(null);
    const liveViewportRef = useRef({ bounds: undefined as MapBoundsState | undefined, zoom: 10 });

    liveViewportRef.current = { bounds, zoom };

    const throttledPushViewportQuery = useRef(
        throttle(() => {
            const { bounds: b, zoom: z } = liveViewportRef.current;
            if (!b) return;
            setViewportQueryBounds(b);
            setViewportQueryZoom(z);
        }, VIEWPORT_REQUEST_THROTTLE_MS),
    );

    useEffect(() => {
        throttledPushViewportQuery.current();
    }, [bounds, zoom]);

    useEffect(() => {
        return () => throttledPushViewportQuery.current.cancel();
    }, []);

    const { currentUser } = useGetCurrentUserV2();
    const { company, isLoading: isCompanyLoading } = useGetCompany({ companyUriId: currentUser?.company });

    const companyCenter = useMemo(() => {
        if (company?.latitude && company?.longitude) {
            const lat = typeof company.latitude === 'string' ? parseFloat(company.latitude) : company.latitude;
            const lng = typeof company.longitude === 'string' ? parseFloat(company.longitude) : company.longitude;
            if (!isNaN(lat) && !isNaN(lng)) {
                return { lat, lng };
            }
        }

        return undefined;
    }, [company]);

    // Server-side clustering
    const { data: viewportData, isFetching: isViewportFetching } = useGetAvatarsViewport({
        bounds: viewportQueryBounds
            ? {
                  north: viewportQueryBounds.nelat,
                  south: viewportQueryBounds.swlat,
                  east: viewportQueryBounds.nelng,
                  west: viewportQueryBounds.swlng,
              }
            : null,
        zoom: viewportQueryZoom,
        enabled: !!viewportQueryBounds,
    });

    const lastPopulatedViewportRef = useRef<ViewportResponse | null>(null);
    if (viewportData?.data?.length) {
        lastPopulatedViewportRef.current = viewportData;
    }

    /** Evita vaciar el mapa si el servidor devuelve `data: []` entre cluster e items, o durante el salto de tipo. */
    const markersViewportImmediate = useMemo((): ViewportResponse | undefined => {
        if (!viewportData) return undefined;
        if (viewportData.data?.length) return viewportData;
        if (isViewportFetching && lastPopulatedViewportRef.current) {
            return lastPopulatedViewportRef.current;
        }
        return viewportData;
    }, [viewportData, isViewportFetching]);

    const markersViewportData = useDeferredValue(markersViewportImmediate);

    const itemStacks = useMemo(() => {
        if (markersViewportData?.type !== 'items') return [];
        return groupViewportItemsByPosition(markersViewportData.data);
    }, [markersViewportData]);

    const itemsSignature = useMemo(() => {
        if (markersViewportData?.type !== 'items') return '';
        return markersViewportData.data.map((i) => i.id).join('|');
    }, [markersViewportData]);

    useEffect(() => {
        closeStackPopover();
    }, [itemsSignature, closeStackPopover]);

    const onLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
    }, []);

    const onUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    const onBoundsChanged = useCallback(() => {
        if (!mapRef.current) return;

        const bounds = mapRef.current.getBounds();
        if (!bounds) return;

        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        setBounds({
            nwlng: sw.lng(),
            selat: sw.lat(),
            selng: ne.lng(),
            nwlat: ne.lat(),
            nelat: ne.lat(),
            nelng: ne.lng(),
            swlat: sw.lat(),
            swlng: sw.lng(),
        });
    }, []);

    const onZoomChanged = useCallback(() => {
        if (!mapRef.current) return;
        setZoom(mapRef.current.getZoom() || 10);
    }, []);

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 1,
                    gridTemplateRows: '1fr',
                    height: '100%',
                    overflowY: 'auto',
                }}
            >
                <CustomBlock label={translate('main.navigation.map')}>
                    {isCompanyLoading || !companyCenter ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                minHeight: 320,
                                color: 'text.secondary',
                            }}
                        >
                            {translate('map.loading')}
                        </Box>
                    ) : companyCenter ? (
                        <Box>
                            <LoadScript googleMapsApiKey={GEOCODE_APIKEY ?? ''} libraries={GOOGLE_MAPS_LIBRARIES}>
                                <GoogleMap
                                    mapContainerStyle={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '320px',
                                    }}
                                    center={companyCenter}
                                    zoom={zoom}
                                    onLoad={onLoad}
                                    onUnmount={onUnmount}
                                    onClick={closeStackPopover}
                                    onIdle={onBoundsChanged}
                                    onZoomChanged={onZoomChanged}
                                    options={{
                                        disableDefaultUI: false,
                                        zoomControl: true,
                                    }}
                                >
                                    {/* Renderizado basado en el tipo de respuesta del servidor */}
                                    {markersViewportData?.type === 'clusters' &&
                                        map(markersViewportData.data, (cluster) => (
                                            <OverlayView
                                                key={`cluster-${cluster.cell}`}
                                                position={{ lat: cluster.lat, lng: cluster.lng }}
                                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                                getPixelPositionOffset={(width, height) => ({
                                                    x: -(width / 2),
                                                    y: -(height / 2),
                                                })}
                                            >
                                                <CustomClusterMarker
                                                    lat={cluster.lat}
                                                    lng={cluster.lng}
                                                    label={String(cluster.count)}
                                                    onClick={() => {
                                                        if (!mapRef.current) return;
                                                        const z = mapRef.current.getZoom() ?? 10;
                                                        mapRef.current.setZoom(z + 2);
                                                        mapRef.current.panTo({ lat: cluster.lat, lng: cluster.lng });
                                                    }}
                                                />
                                            </OverlayView>
                                        ))}

                                    {markersViewportData?.type === 'items' &&
                                        map(itemStacks, (group) => {
                                            if (group.items.length === 1) {
                                                const item = group.items[0];
                                                return (
                                                    <OverlayView
                                                        key={`sitemarker-${item.id}`}
                                                        position={{ lat: item.lat, lng: item.lng }}
                                                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                                        getPixelPositionOffset={(width, height) => ({
                                                            x: -(width / 2),
                                                            y: -(height / 2),
                                                        })}
                                                    >
                                                        <CustomMarker
                                                            lat={item.lat}
                                                            lng={item.lng}
                                                            avatarUriId={composeUri(AVATARS, item.id)}
                                                            onClick={() => setSelectedAvatarId(item.id)}
                                                        />
                                                    </OverlayView>
                                                );
                                            }

                                            return (
                                                <OverlayView
                                                    key={`stack-${group.key}`}
                                                    position={{ lat: group.lat, lng: group.lng }}
                                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                                    getPixelPositionOffset={(width, height) => ({
                                                        x: -(width / 2),
                                                        y: -(height / 2),
                                                    })}
                                                >
                                                    <MapStackedItemsMarker
                                                        count={group.items.length}
                                                        tooltipTitle={translate('map.stack_marker_tooltip', {
                                                            count: group.items.length,
                                                        })}
                                                        onClick={(e) =>
                                                            setStackPopover({
                                                                anchorEl: e.currentTarget,
                                                                items: group.items,
                                                            })
                                                        }
                                                    />
                                                </OverlayView>
                                            );
                                        })}
                                </GoogleMap>
                            </LoadScript>
                        </Box>
                    ) : null}
                </CustomBlock>
            </Box>

            <MapStackItemsPopover
                open={Boolean(stackPopover.anchorEl)}
                anchorEl={stackPopover.anchorEl}
                items={stackPopover.items}
                title={translate('map.stack_popover_title')}
                onClose={closeStackPopover}
                onSelectItem={(id) => setSelectedAvatarId(id)}
            />

            <AvatarInfoDialog avatarId={selectedAvatarId} onClose={() => setSelectedAvatarId(null)} />
        </>
    );
};

export default MapMain;
